'use client';
/**
 * `useActiveWallet` — returns the currently-active wallet with impersonation
 * overlay, relocated from the frontend into the SDK as part of the
 * auth-cutover (spec 009, FILE 14, Group B/C).
 *
 * Reads the wallet list from `useWallets()` (which already applies the
 * impersonation overlay) and the persisted active-wallet id from
 * `useActiveWalletSlot()` (injected from the frontend's Zustand store).
 *
 * When impersonation is active and the target has a primary wallet, a
 * synthetic wallet is returned with a stable id prefixed by `impersonated:`.
 * This keeps telemetry ids stable while letting Solana operation callers
 * get the impersonated target's address.
 *
 * Platform-neutral: reads only `useWallets`, `useActiveWalletSlot`, and
 * `useImpersonationOverlay` — pure React hooks. No web/RN globals. Safe in
 * `src/auth/` core per `.claude/rules/platform-agnostic.md`.
 */
import { useCallback } from 'react';
import { useImpersonationOverlay } from '../impersonation/auth-overlay-context.js';
import { useActiveWalletSlot } from './active-wallet-context.js';
import { resolveAvailableActiveWallet } from './device-availability.js';
import { useWallets } from './use-wallets.js';
// ── Constants ─────────────────────────────────────────────────────────────────
const IMPERSONATED_WALLET_ID_PREFIX = 'impersonated:';
// ── Hook ──────────────────────────────────────────────────────────────────────
/**
 * Returns the active wallet with impersonation overlay applied.
 *
 * Drop-in replacement for the old
 * `frontend/src/hooks/auth/use-active-wallet.ts`.
 * Import from `@morfi/backend-sdk-v2/client`.
 */
export function useActiveWallet() {
    const { wallets, isLoading } = useWallets();
    const { activeWalletId: persistedActiveWalletId, isHydrated, setActiveWalletId: persistActiveWalletId, } = useActiveWalletSlot();
    const { isActive: isImpersonating, target } = useImpersonationOverlay();
    const setActiveWalletId = useCallback((walletId) => {
        if (walletId === persistedActiveWalletId)
            return;
        if (walletId !== null &&
            !wallets.some((wallet) => wallet.id === walletId && wallet.archivedAt === null && wallet.isAvailable)) {
            return;
        }
        persistActiveWalletId(walletId);
    }, [persistActiveWalletId, persistedActiveWalletId, wallets]);
    if (isImpersonating &&
        target !== null &&
        target.primaryWallet !== null &&
        target.primaryWallet.length > 0) {
        const syntheticId = `${IMPERSONATED_WALLET_ID_PREFIX}${target.userId}`;
        const impersonatedWallet = {
            id: syntheticId,
            address: target.primaryWallet,
            label: target.displayName ?? target.handle ?? 'Impersonated Wallet',
            type: 'embedded',
            isAvailable: true,
            isPrimary: true,
            archivedAt: null,
            turnkeySubOrgId: null,
            totalVolume: '0',
            totalTrades: 0,
            realizedPnl: '0',
            unrealizedPnl: '0',
            totalPnl: '0',
            marketsTraded: 0,
            createdAt: '',
        };
        return {
            activeWallet: impersonatedWallet,
            activeWalletId: syntheticId,
            setActiveWalletId: () => { },
        };
    }
    const activeWallet = isHydrated && !isLoading
        ? resolveAvailableActiveWallet(wallets, persistedActiveWalletId)
        : null;
    return {
        activeWallet,
        activeWalletId: activeWallet?.id ?? null,
        setActiveWalletId,
    };
}
//# sourceMappingURL=use-active-wallet.js.map